import { InputSelector, SearchIcon, Table } from '@allurion/ui';
import { isNumber } from '@allurion/utils';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { TrackedEmptyState } from 'src/analytics/TrackedUI';
import { Pagination } from 'src/components/Pagination';
import { PatientsAccess } from 'src/domain/patient/Patients';

import styles from './ManagePatientsTable.module.scss';

type Props = {
  patients: PatientsAccess[];
  totalPages: number;
  onCheckPatient: (pat?: PatientsAccess, isAll?: boolean) => void;
  onSort: (col: any) => void;
  selectedPatients: PatientsAccess[];
  setPage: React.Dispatch<
    React.SetStateAction<{
      index: number;
      size: number;
    }>
  >;
  page: { index: number; size: number };
  sortBy: string;
  defaultPageSize: number;
};

export function ManagePatientsTable({
  patients = [],
  totalPages = 1,
  onCheckPatient = () => {},
  onSort = () => {},
  selectedPatients = [],
  page,
  setPage = () => {},
  sortBy,
  defaultPageSize,
}: Props) {
  const intl = useIntl();

  const updatePage = (nextPage: React.SetStateAction<number>): void => {
    setPage((pg) => ({
      ...pg,
      index: isNumber(nextPage) ? nextPage : nextPage(pg.index),
    }));
  };
  const updatePageSize = (nextPageSize: React.SetStateAction<number>): void => {
    setPage((pg) => ({
      ...pg,
      size: isNumber(nextPageSize) ? nextPageSize : nextPageSize(pg.size),
    }));
  };

  const isSelectedAll = patients?.length > 0 && selectedPatients?.length === patients?.length;
  const selectAll = () => {
    onCheckPatient(undefined, !isSelectedAll);
  };

  if (patients.length === 0) {
    return (
      <TrackedEmptyState
        title={intl.formatMessage({
          id: 'manage-patients.table.empty-title',
          defaultMessage: 'No patients found',
        })}
        subtitle={intl.formatMessage({
          id: 'manage-patients.table.empty-description',
          defaultMessage: 'There are no patients matching your search criteria.',
        })}
        icon={<SearchIcon />}
        trackedLabel="manage-patients.table.empty-state"
      />
    );
  }

  return (
    <div className={styles.container}>
      <Table
        columns={[
          {
            field: 'patient_id',
            title: <InputSelector type="checkbox" onChange={selectAll} checked={isSelectedAll} />,
            formatter: (value, row) => {
              const isSelected = !!selectedPatients?.filter((s) => s.patient_id === value)?.length;
              const handleSelection = () => {
                onCheckPatient(row);
              };

              return (
                <InputSelector type="checkbox" onChange={handleSelection} checked={isSelected} />
              );
            },
            sortable: false,
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.last-name',
              defaultMessage: 'Last Name',
            }),
            field: 'lastname',
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.first-name',
              defaultMessage: 'First Name',
            }),
            field: 'firstname',
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.email',
              defaultMessage: 'Email',
            }),
            field: 'email_address',
            sortable: false,
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.age',
              defaultMessage: 'Age',
            }),
            field: 'age',
            sortable: false,
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.gender',
              defaultMessage: 'Gender',
            }),
            field: 'gender',
            formatter: (value: any) =>
              value ? (value.toLocaleLowerCase() === 'male' ? 'M' : 'F') : '',
            sortable: false,
          },
          {
            title: intl.formatMessage({
              id: 'manage-patients.table.providers',
              defaultMessage: 'Providers',
            }),
            field: 'providers_count',
            sortable: false,
          },
        ]}
        data={patients}
        sortBy={sortBy}
        onSortChange={onSort}
      />

      <Pagination
        page={page.index}
        setPage={updatePage}
        pageSize={page.size}
        setPageSize={updatePageSize}
        lastPage={totalPages}
        defaultPageSize={defaultPageSize}
        pageSizeLabel={intl.formatMessage({
          id: 'manage-patients.table.page-size',
          defaultMessage: 'Patients per page',
        })}
      />
    </div>
  );
}
